// src/components/ProductForm.js
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaCloudUploadAlt } from 'react-icons/fa';

const ProductForm = ({ product, onSuccess, onClose, isOpen }) => {
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    discount: 0,
    stock: '',
    category: '',
    customizable: false,
    size: '',
    yarnDetails: '',
    colors: [],
    deliveryTime: '',
    washCare: '',
    importantNote: '',
  });
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [colorInput, setColorInput] = useState('');

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/settings/categories/all`);
      if (res.ok) {
        const data = await res.json();
        setCategories(data || []);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name || '',
        description: product.description || '',
        price: product.price || '',
        discount: product.discount || 0,
        stock: (product.stock === null || product.stock === undefined) ? '' : product.stock,
        category: product.category || '',
        customizable: product.customizable || false,
        size: product.size || '',
        yarnDetails: product.yarnDetails || '',
        colors: product.colors || [],
        deliveryTime: product.deliveryTime || '',
        washCare: product.washCare || '',
        importantNote: product.importantNote || '',
      });
      setColorInput((product.colors || []).join(', '));

      if (product.imageFilenames && product.imageFilenames.length > 0) {
        setExistingImages(product.imageFilenames);
      } else if (product.imageFilename) {
        setExistingImages([product.imageFilename]);
      }
    } else {
      setFormData({
        name: '', description: '', price: '', discount: 0, stock: '', category: '', customizable: false,
        size: '', yarnDetails: '', colors: [], deliveryTime: '', washCare: '', importantNote: '',
      });
      setColorInput('');
      setExistingImages([]);
      setImages([]);
      setImagePreviews([]);
    }
  }, [product, isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleColors = (e) => {
    setColorInput(e.target.value);
  };

  const handleColorsBlur = () => {
    const parsed = colorInput.split(',').map(c => c.trim()).filter(c => c);
    setFormData({ ...formData, colors: parsed });
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const totalImages = existingImages.length + images.length + files.length;

    if (totalImages > 5) {
      toast.error('Maximum 5 images allowed in total');
      return;
    }

    const newImages = [...images, ...files];
    setImages(newImages);

    const newPreviews = files.map(file => URL.createObjectURL(file));
    setImagePreviews([...imagePreviews, ...newPreviews]);

    e.target.value = '';
  };

  const removeExistingImage = (index) => {
    const updatedExisting = existingImages.filter((_, i) => i !== index);
    setExistingImages(updatedExisting);
  };

  const removeNewImage = (index) => {
    const updatedImages = images.filter((_, i) => i !== index);
    const updatedPreviews = imagePreviews.filter((_, i) => i !== index);
    setImages(updatedImages);
    setImagePreviews(updatedPreviews);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.category) {
      toast.error('Please select a category');
      return;
    }

    const totalImages = existingImages.length + images.length;
    if (totalImages === 0) {
      toast.error('Please upload at least one image');
      return;
    }

    // Ensure colors are parsed from the raw input before submit
    const finalColors = colorInput.split(',').map(c => c.trim()).filter(c => c);

    const data = new FormData();

    const submitFormData = { ...formData, colors: finalColors };

    Object.keys(submitFormData).forEach((key) => {
      if (key === 'colors' && Array.isArray(submitFormData[key])) {
        submitFormData[key].forEach((val) => data.append(`${key}[]`, val));
      } else {
        data.append(key, submitFormData[key]);
      }
    });

    if (product) {
      existingImages.forEach((filename) => {
        data.append('existingImages[]', filename);
      });
    }

    images.forEach((image) => {
      data.append('images', image);
    });

    try {
      const token = localStorage.getItem('token');
      const url = product
        ? `${process.env.REACT_APP_API_URL}/api/products/${product._id}`
        : `${process.env.REACT_APP_API_URL}/api/products`;
      const method = product ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}` },
        body: data,
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || 'Save failed');
      }

      toast.success(`Product ${product ? 'updated' : 'created'} successfully`);

      setFormData({
        name: '', description: '', price: '', discount: 0, stock: '', category: '', customizable: false,
        size: '', yarnDetails: '', colors: [], deliveryTime: '', washCare: '', importantNote: '',
      });
      setImages([]);
      setImagePreviews([]);
      setExistingImages([]);

      onSuccess();
      if (onClose) onClose();
    } catch (err) {
      console.error('Error saving product:', err);
      toast.error(err.message || 'Failed to save product');
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ type: 'spring', damping: 25 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-5 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-white">
              {product ? 'Edit Product' : 'Add New Product'}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="text-white/80 hover:text-white hover:bg-white/10 rounded-lg p-2 transition-all"
            >
              <FaTimes size={20} />
            </button>
          </div>

          {/* Modal Body - Scrollable */}
          <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-80px)]">
            <div className="p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Product Name */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Product Name *</label>
                  <input
                    name="name"
                    placeholder="e.g., Handmade Crochet Beanie"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                    required
                  />
                </div>

                {/* Description */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Description *</label>
                  <textarea
                    name="description"
                    placeholder="Detailed product description"
                    value={formData.description}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                    rows="3"
                    required
                  />
                </div>

                {/* Price */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Price (₹) *</label>
                  <input
                    name="price"
                    type="number"
                    placeholder="499"
                    value={formData.price}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                    required
                  />
                </div>

                {/* Discount */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Discount (%)</label>
                  <input
                    name="discount"
                    type="number"
                    min="0"
                    max="100"
                    placeholder="0"
                    value={formData.discount}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                  />
                  {formData.discount > 0 && formData.price > 0 && (
                    <p className="text-xs text-emerald-600 mt-1 font-medium">
                      Final Price: ₹{Math.round(formData.price * (1 - formData.discount / 100))}
                    </p>
                  )}
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Category *</label>
                  <input
                    name="category"
                    list="categories"
                    value={formData.category}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                    placeholder="Select or type a category"
                    required
                  />
                  <datalist id="categories">
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.name} />
                    ))}
                  </datalist>
                </div>

                {/* Size */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Size</label>
                  <input
                    name="size"
                    placeholder="e.g., Medium, 20x20 cm"
                    value={formData.size}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                  />
                </div>

                {/* Stock */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Stock</label>
                  <input
                    name="stock"
                    type="number"
                    min="0"
                    placeholder="Leave blank for unlimited"
                    value={formData.stock}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                  />
                  <p className="text-xs text-slate-400 mt-1">Blank = inventory not tracked</p>
                </div>

                {/* Customizable */}
                <div className="md:col-span-2">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="customizable"
                      checked={formData.customizable}
                      onChange={handleChange}
                      className="w-5 h-5 text-primary-600 border-slate-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-slate-700">Customizable (Can customer request modifications?)</span>
                  </label>
                </div>

                {/* Yarn Details */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Yarn Details</label>
                  <input
                    name="yarnDetails"
                    placeholder="e.g., 100% Wool"
                    value={formData.yarnDetails}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                  />
                </div>

                {/* Delivery Time */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Delivery Time</label>
                  <input
                    name="deliveryTime"
                    placeholder="e.g., 3-5 business days"
                    value={formData.deliveryTime}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                  />
                </div>

                {/* Colors */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Available Colors (comma separated)</label>
                  <input
                    name="colors"
                    placeholder="e.g., Red, Blue, Green"
                    value={colorInput}
                    onChange={handleColors}
                    onBlur={handleColorsBlur}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                  />
                </div>

                {/* Wash Care */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Wash Care Instructions</label>
                  <input
                    name="washCare"
                    placeholder="e.g., Hand wash only"
                    value={formData.washCare}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                  />
                </div>

                {/* Important Note */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Important Note</label>
                  <textarea
                    name="importantNote"
                    placeholder="Any special instructions or notes"
                    value={formData.importantNote}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                    rows="2"
                  />
                </div>

                {/* Images Section */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Product Images * (Maximum 5 images)
                  </label>

                  {/* Existing Images */}
                  {existingImages.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs text-slate-500 mb-2 font-medium">Existing Images:</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                        {existingImages.map((filename, index) => (
                          <div key={`existing-${index}`} className="relative group">
                            <img
                              src={`${process.env.REACT_APP_API_URL}/api/images/${filename}`}
                              alt={`Existing ${index + 1}`}
                              className="w-full h-24 object-cover rounded-lg border-2 border-slate-200"
                            />
                            <button
                              type="button"
                              onClick={() => removeExistingImage(index)}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600 transition-all shadow-lg"
                            >
                              <FaTimes size={10} />
                            </button>
                            <span className="absolute bottom-1 left-1 bg-primary-500 text-white text-[10px] px-1.5 py-0.5 rounded">
                              #{index + 1}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* New Images */}
                  {imagePreviews.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs text-slate-500 mb-2 font-medium">New Images:</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                        {imagePreviews.map((preview, index) => (
                          <div key={`new-${index}`} className="relative group">
                            <img
                              src={preview}
                              alt={`New ${index + 1}`}
                              className="w-full h-24 object-cover rounded-lg border-2 border-emerald-300"
                            />
                            <button
                              type="button"
                              onClick={() => removeNewImage(index)}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600 transition-all shadow-lg"
                            >
                              <FaTimes size={10} />
                            </button>
                            <span className="absolute bottom-1 left-1 bg-emerald-500 text-white text-[10px] px-1.5 py-0.5 rounded">
                              New
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Upload Button */}
                  {(existingImages.length + images.length) < 5 && (
                    <label className="block w-full cursor-pointer">
                      <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-primary-400 hover:bg-primary-50/50 transition-all">
                        <FaCloudUploadAlt className="mx-auto text-3xl text-slate-400 mb-2" />
                        <p className="text-sm text-slate-600 font-medium">
                          Click to add images ({existingImages.length + images.length}/5)
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          PNG, JPG up to 5MB each
                        </p>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageChange}
                        className="hidden"
                      />
                    </label>
                  )}

                  {(existingImages.length + images.length) === 5 && (
                    <p className="text-xs text-emerald-600 font-medium flex items-center gap-1">
                      <span>✓</span> Maximum images reached (5/5)
                    </p>
                  )}

                  {(existingImages.length + images.length) === 0 && (
                    <p className="text-xs text-red-500 mt-2">
                      Please upload at least one image
                    </p>
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <div className="sticky bottom-0 bg-white border-t border-slate-100 pt-5 mt-6">
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 bg-slate-100 text-slate-700 px-6 py-3 rounded-xl hover:bg-slate-200 font-medium transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-gradient-to-r from-primary-500 to-primary-600 text-white px-6 py-3 rounded-xl hover:from-primary-600 hover:to-primary-700 font-medium transition-all shadow-lg shadow-primary-500/30"
                  >
                    {product ? 'Update Product' : 'Create Product'}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ProductForm;
